import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  SubdirectoryArrowRight as SubIcon,
} from '@mui/icons-material';

interface Section {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  instructions?: any;
  language_id: number;
}

interface SubSection {
  id: string;
  section_id: string;
  name: string;
  description: string;
  is_active: boolean;
  instructions?: any;
  language_id: number;
}

const SectionsManagement: React.FC = () => {
  const navigate = useNavigate();
  
  const [sections, setSections] = useState<Section[]>([]);
  const [subsections, setSubsections] = useState<SubSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [subsectionDialogOpen, setSubsectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [editingSubsection, setEditingSubsection] = useState<SubSection | null>(null);
  
  // Form states
  const [sectionForm, setSectionForm] = useState({
    name: '',
    description: '',
    instructions: '',
  });
  
  const [subsectionForm, setSubsectionForm] = useState({
    section_id: '',
    name: '',
    description: '',
    instructions: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load sections
      const sectionsResponse = await fetch(API_ENDPOINTS.SECTIONS);
      const sectionsData = await sectionsResponse.json();
      
      if (sectionsData.status === 'success') {
        setSections(sectionsData.data);
      }

      // Load subsections
      const subsectionsResponse = await fetch(API_ENDPOINTS.SUBSECTIONS);
      const subsectionsData = await subsectionsResponse.json();
      
      if (subsectionsData.status === 'success') {
        setSubsections(subsectionsData.data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load sections and subsections');
    } finally {
      setLoading(false);
    }
  };

  // Section CRUD operations
  const handleCreateSection = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.CREATE_SECTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionForm),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Section created successfully!');
        setSectionDialogOpen(false);
        resetSectionForm();
        loadData();
      } else {
        setError(result.message || 'Failed to create section');
      }
    } catch (err) {
      setError('Failed to create section');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSection = async () => {
    if (!editingSection) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.UPDATE_SECTION(editingSection.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionForm),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Section updated successfully!');
        setSectionDialogOpen(false);
        setEditingSection(null);
        resetSectionForm();
        loadData();
      } else {
        setError(result.message || 'Failed to update section');
      }
    } catch (err) {
      setError('Failed to update section');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this section? This will also affect all related subsections and questions.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.DELETE_SECTION(sectionId), {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Section deleted successfully!');
        loadData();
      } else {
        setError(result.message || 'Failed to delete section');
      }
    } catch (err) {
      setError('Failed to delete section');
    } finally {
      setLoading(false);
    }
  };

  // Subsection CRUD operations
  const handleCreateSubsection = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.CREATE_SUBSECTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subsectionForm),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Subsection created successfully!');
        setSubsectionDialogOpen(false);
        resetSubsectionForm();
        loadData();
      } else {
        setError(result.message || 'Failed to create subsection');
      }
    } catch (err) {
      setError('Failed to create subsection');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubsection = async () => {
    if (!editingSubsection) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.UPDATE_SUBSECTION(editingSubsection.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subsectionForm),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Subsection updated successfully!');
        setSubsectionDialogOpen(false);
        setEditingSubsection(null);
        resetSubsectionForm();
        loadData();
      } else {
        setError(result.message || 'Failed to update subsection');
      }
    } catch (err) {
      setError('Failed to update subsection');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubsection = async (subsectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this subsection? This will also affect all related questions.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.DELETE_SUBSECTION(subsectionId), {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setSuccess('Subsection deleted successfully!');
        loadData();
      } else {
        setError(result.message || 'Failed to delete subsection');
      }
    } catch (err) {
      setError('Failed to delete subsection');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const resetSectionForm = () => {
    setSectionForm({ name: '', description: '', instructions: '' });
  };

  const resetSubsectionForm = () => {
    setSubsectionForm({ section_id: '', name: '', description: '', instructions: '' });
  };

  const openSectionDialog = (section?: Section) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        name: section.name,
        description: section.description || '',
        instructions: section.instructions || '',
      });
    } else {
      setEditingSection(null);
      resetSectionForm();
    }
    setSectionDialogOpen(true);
  };

  const openSubsectionDialog = (subsection?: SubSection) => {
    if (subsection) {
      setEditingSubsection(subsection);
      setSubsectionForm({
        section_id: subsection.section_id,
        name: subsection.name,
        description: subsection.description || '',
        instructions: subsection.instructions || '',
      });
    } else {
      setEditingSubsection(null);
      resetSubsectionForm();
    }
    setSubsectionDialogOpen(true);
  };

  const getSubsectionsForSection = (sectionId: string) => {
    return subsections.filter(sub => sub.section_id === sectionId && sub.is_active);
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/questions')}
          variant="outlined"
        >
          Back to Questions
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" fontWeight={800} className="gradient-text" gutterBottom>
            📁 Sections & Subsections Management
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage assessment sections and their subsections
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openSectionDialog()}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' }
          }}
        >
          Add Section
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openSubsectionDialog()}
          sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #e879f9 0%, #ef4444 100%)' }
          }}
        >
          Add Subsection
        </Button>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Sections and Subsections Display */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600}>
              📋 Sections & Subsections ({sections.length} sections, {subsections.length} subsections)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading data...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              {sections.map((section) => (
                <Accordion key={section.id} sx={{ mb: 2, borderRadius: '8px !important' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      bgcolor: 'primary.50',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: 'primary.100' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <FolderIcon color="primary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {section.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {section.description || 'No description'}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${getSubsectionsForSection(section.id).length} subsections`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSectionDialog(section);
                          }}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section.id);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SubIcon /> Subsections
                    </Typography>
                    
                    {getSubsectionsForSection(section.id).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 3 }}>
                        No subsections found for this section
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Name</strong></TableCell>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getSubsectionsForSection(section.id).map((subsection) => (
                              <TableRow key={subsection.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {subsection.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {subsection.description || 'No description'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => openSubsectionDialog(subsection)}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSubsection(subsection.id)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
              
              {sections.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No sections found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create your first section to get started
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Section Dialog */}
      <Dialog 
        open={sectionDialogOpen} 
        onClose={() => setSectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {editingSection ? '✏️ Edit Section' : '➕ Add New Section'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Section Name"
              value={sectionForm.name}
              onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
              placeholder="e.g., Listening Section"
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={sectionForm.description}
              onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
              placeholder="Brief description of this section..."
            />
            
            <TextField
              fullWidth
              label="Instructions (Optional)"
              multiline
              rows={3}
              value={sectionForm.instructions}
              onChange={(e) => setSectionForm({ ...sectionForm, instructions: e.target.value })}
              placeholder="Special instructions for this section..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={editingSection ? handleUpdateSection : handleCreateSection}
            disabled={loading || !sectionForm.name}
          >
            {loading ? <CircularProgress size={20} /> : (editingSection ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subsection Dialog */}
      <Dialog 
        open={subsectionDialogOpen} 
        onClose={() => setSubsectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {editingSubsection ? '✏️ Edit Subsection' : '➕ Add New Subsection'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Parent Section</InputLabel>
              <Select
                value={subsectionForm.section_id}
                onChange={(e) => setSubsectionForm({ ...subsectionForm, section_id: e.target.value })}
              >
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Subsection Name"
              value={subsectionForm.name}
              onChange={(e) => setSubsectionForm({ ...subsectionForm, name: e.target.value })}
              placeholder="e.g., Audio Comprehension"
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={subsectionForm.description}
              onChange={(e) => setSubsectionForm({ ...subsectionForm, description: e.target.value })}
              placeholder="Brief description of this subsection..."
            />
            
            <TextField
              fullWidth
              label="Instructions (Optional)"
              multiline
              rows={3}
              value={subsectionForm.instructions}
              onChange={(e) => setSubsectionForm({ ...subsectionForm, instructions: e.target.value })}
              placeholder="Special instructions for this subsection..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubsectionDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={editingSubsection ? handleUpdateSubsection : handleCreateSubsection}
            disabled={loading || !subsectionForm.name || !subsectionForm.section_id}
          >
            {loading ? <CircularProgress size={20} /> : (editingSubsection ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Buttons */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 80, right: 24 }}
        onClick={() => openSectionDialog()}
      >
        <FolderIcon />
      </Fab>
      
      <Fab
        color="secondary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => openSubsectionDialog()}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default SectionsManagement;
